import React, { useState, useEffect, useCallback, useRef } from 'react';
import { websocketVirtualAgent } from 'components/virtualagentws/websocketVirtualAgent';
import { useHVAHealthCare } from './HVAHealthCareContext';
import { FeedbackMessage } from './types';
import { healthCareService } from './health-service';
import { Col, Row } from 'react-bootstrap';
import Button from 'components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMicrophone,
  faMicrophoneSlash,
  faPhoneSlash,
  faPhoneVolume,
  faInfoCircle,
  faCalendarCheck,
  faUserMd,
  faMicroscope,
  faMapMarkerAlt
} from '@fortawesome/free-solid-svg-icons';
import {
  getVirtualAgentWsUrl,
  getAuthenticatedWsUrl
} from 'services/virtualagent.service';

// Interfaces for messages and conversation status
interface ConversationMessage {
  source: 'user' | 'ai';
  message: string;
  timestamp: Date;
}

interface ConversationMode {
  mode: 'speaking' | 'listening';
}

// Props for the widget component
type HVAHealthCareWidgetProps = {
  agentId: string;
  agentName: string;
  onError?: (error: Error) => void;
  onFeedback: (message: Omit<FeedbackMessage, 'id' | 'timestamp'>) => void;
  firstMessage: string;
  prompt: string;
  voice: string;
  language: string;
};

/**
 * Conversation widget that handles verbal interaction with the virtual agent.
 * Implements client tool logic that the agent can use.
 */
const HVAHealthCareWidget = ({
  agentId,
  agentName,
  onError,
  onFeedback,
  firstMessage,
  prompt,
  voice,
  language
}: HVAHealthCareWidgetProps): JSX.Element => {
  // State for permissions and conversation
  const [hasPermission, setHasPermission] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [clientToolsConfig, setClientToolsConfig] = useState({});
  const [isListening, setIsListening] = useState(false);

  // Get full conversation context
  const {
    patientInfo,
    setPatientInfo,
    isAmkaValidated,
    setIsAmkaValidated,
    conversationStage,
    setConversationStage,
    setSelectedService,
    setSelectedLocation,
    setSelectedDoctor,
    setSelectedExamination,
    setAvailableSlots,
    setBookedAppointment,
    addSystemMessage
  } = useHVAHealthCare();

  // Reference for messages container (for automatic scrolling)
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Function to scroll to last message
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      const scrollContainer = messagesContainerRef.current;
      scrollContainer.scrollTop = scrollContainer.scrollHeight;
    }
  };

  // Update clientTools whenever local validations change
  useEffect(() => {
    // Create client tools configuration that the agent can use
    setClientToolsConfig({
      // To handle actions requested by the agent
      // eslint-disable-next-line
      requestAction: async (parameters: { requestedAction: string; appointmentDetails?: any }) => {
        const { requestedAction, appointmentDetails } = parameters;

        switch (requestedAction) {
          // Action to book an appointment
          case 'BOOKAPPOINTMENT': {
            if (!appointmentDetails || !appointmentDetails.slotId) {
              onFeedback({
                type: 'appointment',
                message: 'Insufficient data to book appointment',
                success: false
              });
              return JSON.stringify({
                success: false,
                message: 'Slot ID required for booking'
              });
            }

            // Try to book the appointment
            const bookingResult = healthCareService.bookAppointment(
              appointmentDetails.slotId,
              patientInfo || {
                voice: voice,
                amka: '',
                name: '',
                phoneNumber: '',
                appointmentType: 'information'
              }
            );

            if (bookingResult.success && bookingResult.appointment) {
              // Update booked appointment state
              setBookedAppointment(bookingResult.appointment);
              setConversationStage('finished');

              // Get complete appointment details
              const details = healthCareService.getAppointmentDetails(
                appointmentDetails.slotId
              );

              let serviceDetails = '';
              if (details.serviceType === 'examination') {
                // eslint-disable-next-line
                const exam = details.service as any;
                serviceDetails = `Examination: ${exam?.name?.el}`;
              } else if (details.serviceType === 'doctor') {
                // eslint-disable-next-line
                const doctor = details.service as any;
                serviceDetails = `Doctor: ${doctor?.firstName?.el} ${doctor?.lastName?.el} (${details.specialty?.name.el})`;
              }

              onFeedback({
                type: 'appointment',
                message: 'Appointment booked successfully',
                details: `Date: ${details.slot?.date} | Time: ${details.slot?.startTime} | Location: ${details.location?.name} | ${serviceDetails}`,
                success: true
              });

              return JSON.stringify({
                success: true,
                message: 'Appointment booked successfully',
                data: {
                  appointmentDate: details.slot?.date,
                  appointmentTime: details.slot?.startTime,
                  location: details.location?.name,
                  service: serviceDetails
                }
              });
            } else {
              onFeedback({
                type: 'appointment',
                message: 'Error booking appointment',
                details: bookingResult.message,
                success: false
              });

              return JSON.stringify({
                success: false,
                message: bookingResult.message
              });
            }
          }

          // Action to send instructions to patient
          case 'SENDINSTRUCTIONS': {
            if (!patientInfo?.phoneNumber) {
              onFeedback({
                type: 'info',
                message: 'Could not send instructions',
                details: 'No phone number registered',
                success: false
              });
              return JSON.stringify({
                success: false,
                message: 'No phone number registered'
              });
            }

            onFeedback({
              type: 'info',
              message: 'Instructions sent',
              details: `Instructions have been sent to phone number ${patientInfo.phoneNumber}`,
              success: true
            });
            return JSON.stringify({
              success: true,
              message: 'Instructions sent successfully'
            });
          }

          // Default action for cases not covered
          default: {
            onFeedback({
              type: 'info',
              message: `Requested action not available: ${requestedAction}`,
              details: 'Requested action not available',
              success: false
            });
            return JSON.stringify({
              success: false,
              data: null,
              message: `Requested action not available: '${requestedAction}'`
            });
          }
        }
      },

      // To request information from the system
      // eslint-disable-next-line
      requestInfo: async (parameters: { requestedInfo: string; filters?: any }) => {
        const { requestedInfo, filters } = parameters;
        const requestSummary = `Request: ${requestedInfo}${
          filters ? `, Filters: ${JSON.stringify(filters)}` : ''
        }`;

        switch (requestedInfo) {
          // Request actual date and time
          case 'DATETIME': {
            // Retorna fecha y hora actuales
            const now = new Date();
            const options: Intl.DateTimeFormatOptions = {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            };
            const dateTimeString = now.toLocaleString('en-GB', options);

            onFeedback({
              type: 'info',
              message: 'Date and Time Requested',
              details: `Actual date and time: ${dateTimeString}`,
              success: true
            });

            return JSON.stringify({
              success: true,
              data: dateTimeString,
              message: ''
            });
          }

          // Request available locations
          case 'AVAILABLELOCATIONS': {
            // Si existe un locationId en los filtros, devolver solo esa ubicación
            if (filters?.locationId) {
              const location = healthCareService.getLocationById(
                filters.locationId
              );

              if (location) {
                setSelectedLocation(filters.locationId);

                onFeedback({
                  type: 'info',
                  message: 'Specific location requested',
                  details: `Location ${location.name} found`,
                  success: true
                });

                return JSON.stringify({
                  success: true,
                  data: [
                    {
                      id: location.id,
                      name: location.name,
                      area: location.area,
                      type: location.type,
                      address: location.address
                    }
                  ],
                  message: ''
                });
              } else {
                onFeedback({
                  type: 'info',
                  message: 'Specific location requested',
                  details: `Location with ID ${filters.locationId} not found`,
                  success: false
                });

                return JSON.stringify({
                  success: false,
                  message: `Location with ID ${filters.locationId} not found`,
                  data: []
                });
              }
            } else {
              // Código original para listar todas las ubicaciones
              const locations = healthCareService.getLocations();

              onFeedback({
                type: 'info',
                message: 'Available locations requested',
                details: `${locations.length} locations found`,
                success: true
              });

              return JSON.stringify({
                success: true,
                data: locations.map(loc => ({
                  id: loc.id,
                  name: loc.name,
                  area: loc.area,
                  type: loc.type,
                  address: loc.address
                })),
                message: ''
              });
            }
          }

          // Request available examinations
          case 'AVAILABLEEXAMINATIONS': {
            // Si existe un examinationId en los filtros, devolver solo ese examen
            if (filters?.examinationId) {
              const examination = healthCareService.getExaminationById(
                filters.examinationId
              );

              if (examination) {
                setSelectedExamination(filters.examinationId);
                setSelectedService(filters.examinationId);

                // Update appointment type in patient information
                if (patientInfo) {
                  setPatientInfo({
                    ...patientInfo,
                    appointmentType: 'examination',
                    examinationType: filters.examinationId
                  });
                }

                // Advance conversation stage if we're in service selection
                if (conversationStage === 'service_selection') {
                  setConversationStage('scheduling');
                }

                onFeedback({
                  type: 'info',
                  message: 'Specific examination requested',
                  details: `Examination ${examination.name.el} found`,
                  success: true
                });

                return JSON.stringify({
                  success: true,
                  data: [
                    {
                      id: examination.id,
                      name: examination.name.el,
                      nameEn: examination.name.en,
                      code: examination.code,
                      duration: examination.duration,
                      preparationNeeded: examination.preparationNeeded,
                      preparationInstructions:
                        examination.preparationInstructions?.el
                    }
                  ],
                  message: ''
                });
              } else {
                onFeedback({
                  type: 'info',
                  message: 'Specific examination requested',
                  details: `Examination with ID ${filters.examinationId} not found`,
                  success: false
                });

                return JSON.stringify({
                  success: false,
                  message: `Examination with ID ${filters.examinationId} not found`,
                  data: []
                });
              }
            } else {
              // Código original para listar todos los exámenes
              const examinations = healthCareService.getExaminations();

              onFeedback({
                type: 'info',
                message: 'Available examinations requested',
                details: `${examinations.length} examinations found`,
                success: true
              });

              return JSON.stringify({
                success: true,
                data: examinations.map(exam => ({
                  id: exam.id,
                  name: exam.name.el,
                  nameEn: exam.name.en,
                  code: exam.code,
                  duration: exam.duration,
                  preparationNeeded: exam.preparationNeeded,
                  preparationInstructions: exam.preparationInstructions?.el
                })),
                message: ''
              });
            }
          }

          // Request medical specialties
          case 'AVAILABLESPECIALTIES': {
            // Si existe un specialtyId en los filtros, devolver solo esa especialidad
            if (filters?.specialtyId) {
              const specialty = healthCareService.getSpecialtyById(
                filters.specialtyId
              );

              if (specialty) {
                setSelectedService(filters.specialtyId);

                // Update appointment type in patient information
                if (patientInfo) {
                  setPatientInfo({
                    ...patientInfo,
                    appointmentType: 'doctor',
                    specialistType: filters.specialtyId
                  });
                }

                onFeedback({
                  type: 'info',
                  message: 'Specific specialty requested',
                  details: `Specialty ${specialty.name.el} found`,
                  success: true
                });

                return JSON.stringify({
                  success: true,
                  data: [
                    {
                      id: specialty.id,
                      name: specialty.name.el,
                      nameEn: specialty.name.en
                    }
                  ],
                  message: ''
                });
              } else {
                onFeedback({
                  type: 'info',
                  message: 'Specific specialty requested',
                  details: `Specialty with ID ${filters.specialtyId} not found`,
                  success: false
                });

                return JSON.stringify({
                  success: false,
                  message: `Specialty with ID ${filters.specialtyId} not found`,
                  data: []
                });
              }
            } else {
              // Código original para listar todas las especialidades
              const specialties = healthCareService.getSpecialties();

              onFeedback({
                type: 'info',
                message: 'Medical specialties requested',
                details: `${specialties.length} specialties found`,
                success: true
              });

              return JSON.stringify({
                success: true,
                data: specialties.map(spec => ({
                  id: spec.id,
                  name: spec.name.el,
                  nameEn: spec.name.en
                })),
                message: ''
              });
            }
          }

          // Request doctors by specialty
          case 'DOCTORSBYSPECIALTY': {
            if (!filters || !filters.specialtyId) {
              onFeedback({
                type: 'info',
                message: 'Missing specialty ID',
                details: 'Specialty ID required to search doctors',
                success: false
              });
              return JSON.stringify({
                success: false,
                message: 'Specialty ID required'
              });
            }

            const doctors = healthCareService.getDoctorsBySpecialty(
              filters.specialtyId
            );

            // Si existe un doctorId en los filtros, devolver solo ese doctor
            if (filters.doctorId) {
              const doctor = doctors.find(doc => doc.id === filters.doctorId);

              if (doctor) {
                setSelectedDoctor(filters.doctorId);

                // Update patient information
                if (patientInfo) {
                  setPatientInfo({
                    ...patientInfo,
                    doctorId: filters.doctorId
                  });
                }

                // Advance conversation stage if we're in service selection
                if (conversationStage === 'service_selection') {
                  setConversationStage('scheduling');
                }

                onFeedback({
                  type: 'info',
                  message: 'Specific doctor requested',
                  details: `Doctor ${doctor.firstName.el} ${doctor.lastName.el} found`,
                  success: true
                });

                return JSON.stringify({
                  success: true,
                  data: [
                    {
                      id: doctor.id,
                      firstName: doctor.firstName.el,
                      lastName: doctor.lastName.el,
                      locationIds: doctor.locationIds,
                      languages: doctor.languages
                    }
                  ],
                  message: ''
                });
              } else {
                onFeedback({
                  type: 'info',
                  message: 'Specific doctor requested',
                  details: `Doctor with ID ${filters.doctorId} not found in specialty ${filters.specialtyId}`,
                  success: false
                });

                return JSON.stringify({
                  success: false,
                  message: `Doctor with ID ${filters.doctorId} not found in specialty ${filters.specialtyId}`,
                  data: []
                });
              }
            } else {
              // Código original para listar todos los médicos de una especialidad
              onFeedback({
                type: 'info',
                message: 'Doctors by specialty requested',
                details: `${doctors.length} doctors found for specialty ${filters.specialtyId}`,
                success: true
              });

              return JSON.stringify({
                success: true,
                data: doctors.map(doc => ({
                  id: doc.id,
                  firstName: doc.firstName.el,
                  lastName: doc.lastName.el,
                  locationIds: doc.locationIds,
                  languages: doc.languages
                })),
                message: ''
              });
            }
          }

          // Request available slots
          case 'AVAILABLESLOTS': {
            if (!filters || !filters.serviceType || !filters.serviceId) {
              onFeedback({
                type: 'info',
                message: `Insufficient data to search time slots. ${requestSummary} | Service type and service ID required`,
                success: false
              });
              return JSON.stringify({
                success: false,
                message: 'Insufficient data to search time slots'
              });
            }

            // If agent specified a location, update state
            if (filters.locationId) {
              setSelectedLocation(filters.locationId);
            }

            // Search available slots based on criteria
            const slots = healthCareService.findAvailableSlots({
              date: filters.date,
              locationId: filters.locationId,
              serviceType: filters.serviceType,
              serviceId: filters.serviceId
            });

            // Update conversation stage progress
            if (
              conversationStage === 'service_selection' ||
              conversationStage === 'identification'
            ) {
              setConversationStage('scheduling');
            }

            // Filter only non-booked slots
            const availableSlots = slots.filter(slot => !slot.booked);

            // Save slots in context for future access
            setAvailableSlots(availableSlots);

            onFeedback({
              type: 'info',
              message: 'Available time slots requested',
              details: `${requestSummary} | ${availableSlots.length} time slots found`,
              success: true
            });

            return JSON.stringify({
              success: true,
              data: availableSlots,
              message: ''
            });
          }

          // Request alternatives when no availability
          case 'ALTERNATIVESLOTS': {
            if (
              !filters ||
              !filters.preferredDate ||
              !filters.preferredTime ||
              !filters.preferredLocationId ||
              !filters.serviceType ||
              !filters.serviceId
            ) {
              onFeedback({
                type: 'info',
                message: 'Insufficient data to search alternatives',
                details: 'Complete preferences required',
                success: false
              });
              return JSON.stringify({
                success: false,
                message: 'Insufficient data to search alternatives'
              });
            }

            // Search alternatives
            const alternatives = healthCareService.findAlternatives({
              preferredDate: filters.preferredDate,
              preferredTime: filters.preferredTime,
              preferredLocationId: filters.preferredLocationId,
              serviceType: filters.serviceType,
              serviceId: filters.serviceId
            });

            // Save alternative slots in context
            if (alternatives.slots.length > 0) {
              setAvailableSlots(alternatives.slots);
            }

            // Update conversation stage
            if (
              alternatives.slots.length > 0 &&
              conversationStage === 'scheduling'
            ) {
              setConversationStage('confirmation');
            }

            onFeedback({
              type: 'info',
              message: 'Alternatives requested',
              details: `Alternative type: ${alternatives.type} | ${alternatives.slots.length} alternatives found`,
              success: alternatives.slots.length > 0
            });

            return JSON.stringify({
              success: true,
              data: alternatives,
              message: ''
            });
          }

          // Handle uncovered information requests
          default: {
            onFeedback({
              type: 'info',
              message: `Requested information not available: ${requestedInfo}`,
              details: 'Requested information not available',
              success: false
            });
            return JSON.stringify({
              success: false,
              data: null,
              message: `Requested information not available: '${requestedInfo}'`
            });
          }
        }
      },

      END_CALL: async (parameters: { reason: string }) => {
        // Añadimos mensaje de feedback
        onFeedback({
          type: 'close',
          message: 'Finalización de llamada por el agente',
          details: `Motivo: ${parameters.reason}`,
          success: true
        });

        return JSON.stringify({
          success: true,
          message: `Finalización de llamada`
        });
      }
    });
  }, [
    patientInfo,
    setPatientInfo,
    onFeedback,
    isAmkaValidated,
    setIsAmkaValidated,
    setAvailableSlots,
    conversationStage,
    setConversationStage,
    setSelectedService,
    setSelectedLocation,
    setSelectedDoctor,
    setSelectedExamination,
    setBookedAppointment,
    voice
  ]);

  // Conversation hook configuration
  const conversation = websocketVirtualAgent({
    onConnect: () => {
      console.log('Connected to agent');
      setIsInitialized(true);
      setIsLoading(false);

      // Add system message
      addSystemMessage('info', 'Connection established with agent');
    },
    onDisconnect: () => {
      console.log('Disconnected from agent');
      onFeedback({
        type: 'close',
        message: 'Call ended',
        success: true
      });
      setIsInitialized(false);
      setIsLoading(false);

      // Add system message
      addSystemMessage('info', 'Call ended');
    },
    // eslint-disable-next-line
    onError: (message: string, context?: any) => {
      console.error('Conversation error:', message, context);
      setIsLoading(false);
      if (onError) {
        onError(new Error(message));
      }

      // Add system message
      addSystemMessage('error', `Error: ${message}`);
    },
    onMessage: message => {
      // Add message to conversation with timestamp
      setMessages(prev => [
        ...prev,
        {
          source: message.source,
          message: message.message,
          timestamp: new Date()
        }
      ]);

      // Automatic scrolling
      setTimeout(scrollToBottom, 0);
    },
    onModeChange: ({ mode }: ConversationMode) => {
      // console.log('Mode changed:', mode);
      setIsListening(mode === 'listening');
    },
    // Add client tools configuration
    clientTools: clientToolsConfig
  });

  // Request microphone permission
  const requestMicPermission = async (): Promise<boolean> => {
    try {
      await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      setHasPermission(true);
      return true;
    } catch (error) {
      console.error('Microphone permission denied:', error);
      setHasPermission(false);
      if (onError) {
        onError(new Error('Microphone permission denied'));
      }

      // Add system message
      addSystemMessage('error', 'Microphone permission denied');

      return false;
    }
  };

  // Start conversation
  const startConversation = useCallback(async (): Promise<void> => {
    if (!hasPermission) {
      const permitted = await requestMicPermission();
      if (!permitted) return;
    }

    try {
      setIsLoading(true);
      // Reset important states at the start of a new conversation
      setIsAmkaValidated(true);
      setConversationStage('identification');
      setBookedAppointment(null);

      // End previous session if exists
      await conversation.endSession();

      // Obtener URL firmada
      let signedUrl;
      if (import.meta.env.VITE_NODE_ENV === 'production') {
        // En producción, usar la función asíncrona
        signedUrl = await getAuthenticatedWsUrl();
      } else {
        // En desarrollo, usar la URL directa
        signedUrl = getVirtualAgentWsUrl();
      }

      // Clear previous messages
      setMessages([]);

      // Start session with signed URL
      await conversation.startSession({
        signedUrl,
        overrides: {
          agent: {
            prompt: {
              prompt: prompt
            },
            firstMessage: firstMessage,
            language: language,
            tools: [
              {
                id: 'requestAction',
                name: 'requestAction',
                description: 'Request a specific action to be performed',
                parameters: {
                  type: 'object',
                  properties: {
                    requestedAction: {
                      type: 'string',
                      description:
                        'The action to request (BOOKAPPOINTMENT, SENDINSTRUCTIONS)'
                    },
                    appointmentDetails: {
                      type: 'object',
                      description:
                        'Details required for booking an appointment (required for BOOKAPPOINTMENT)',
                      properties: {
                        slotId: {
                          type: 'string',
                          description:
                            'ID of the selected time slot for the appointment'
                        }
                      }
                    }
                  },
                  required: ['requestedAction']
                }
              },
              {
                id: 'requestInfo',
                name: 'requestInfo',
                description:
                  'Request specific information from the healthcare system',
                parameters: {
                  type: 'object',
                  properties: {
                    requestedInfo: {
                      type: 'string',
                      description:
                        'The type of information to request (DATETIME, AVAILABLELOCATIONS, AVAILABLEEXAMINATIONS, AVAILABLESPECIALTIES, DOCTORSBYSPECIALTY, AVAILABLESLOTS, ALTERNATIVESLOTS)'
                    },
                    filters: {
                      type: 'object',
                      description:
                        'Optional filters to narrow down the search results',
                      properties: {
                        locationId: {
                          type: 'string',
                          description: 'ID of a specific location'
                        },
                        examinationId: {
                          type: 'string',
                          description: 'ID of a specific examination'
                        },
                        specialtyId: {
                          type: 'string',
                          description: 'ID of a medical specialty'
                        },
                        doctorId: {
                          type: 'string',
                          description: 'ID of a specific doctor'
                        },
                        date: {
                          type: 'string',
                          description: 'Date in YYYY-MM-DD format'
                        },
                        serviceType: {
                          type: 'string',
                          description: 'Type of service (examination or doctor)'
                        },
                        serviceId: {
                          type: 'string',
                          description: 'ID of the requested service'
                        },
                        preferredDate: {
                          type: 'string',
                          description:
                            'Preferred date for alternatives in YYYY-MM-DD format'
                        },
                        preferredTime: {
                          type: 'string',
                          description:
                            'Preferred time for alternatives in HH:MM format'
                        },
                        preferredLocationId: {
                          type: 'string',
                          description:
                            'ID of the preferred location for alternatives'
                        }
                      }
                    }
                  },
                  required: ['requestedInfo']
                }
              },
              {
                id: 'END_CALL',
                name: 'END_CALL',
                description: 'Ends current conversation',
                parameters: {
                  type: 'object',
                  properties: {
                    reason: {
                      type: 'string',
                      description: 'The reason for ending the call'
                    }
                  },
                  required: []
                }
              }
            ]
          },
          tts: {
            voiceId: patientInfo?.voice || voice
          }
        }
      });

      // Add system message
      addSystemMessage('info', 'Conversation started');
    } catch (error) {
      console.error('Failed to start conversation:', error);
      if (error instanceof Error && onError) {
        onError(error);
      }

      // Add system message
      addSystemMessage(
        'error',
        `Error starting conversation: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );

      setIsLoading(false);
    }
  }, [
    agentId,
    conversation,
    hasPermission,
    onError,
    patientInfo,
    setIsAmkaValidated,
    setConversationStage,
    setBookedAppointment,
    voice,
    prompt,
    firstMessage,
    language,
    agentName,
    addSystemMessage
  ]);

  // End conversation
  const endConversation = useCallback(async (): Promise<void> => {
    try {
      await conversation.endSession();
      // No llamamos a addSystemMessage aquí para evitar actualizar estado
      // después de posible desmontaje
    } catch (error) {
      console.error('Failed to end conversation:', error);
      if (error instanceof Error && onError) {
        onError(error);
      }
    }
  }, [conversation, onError]);

  // Effect to initialize scroll when messages mount
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Effect to access microphone when component mounts
  useEffect(() => {
    void requestMicPermission();

    // Cleanup when unmounting
    return () => {
      if (isInitialized) {
        // Usa una versión sincrónica para evitar promesas durante desmontaje
        try {
          conversation.endSession().catch(err => {
            // Solo registramos el error, no actualizamos estado
            console.error('Error during cleanup:', err);
          });
        } catch (error) {
          console.error('Failed during cleanup:', error);
        }
      }
    };
  }, []);

  // Render component
  return (
    <div className="d-flex flex-column h-100">
      <Row className="mb-3">
        <Col className="pe-0" style={{ maxWidth: '170px', width: '170px' }}>
          <div
            className={`d-flex ${
              isInitialized ? 'bg-danger' : 'bg-primary'
            } rounded-5 w-100 justify-content-center`}
          >
            {!isInitialized ? (
              <Button
                onClick={() => void startConversation()}
                className="p-2 text-decoration-none text-white"
                variant="link"
                disabled={!hasPermission || isLoading}
              >
                <FontAwesomeIcon icon={faPhoneVolume} className="me-2 fs-8" />
                {isLoading ? 'Connecting...' : 'Start Call'}
              </Button>
            ) : (
              <Button
                onClick={() => void endConversation()}
                className="p-2 text-decoration-none text-white"
                variant="link"
              >
                <FontAwesomeIcon icon={faPhoneSlash} className="me-2 fs-8" />
                End Call
              </Button>
            )}
          </div>
        </Col>
        <Col className="d-flex justify-content-end">
          <div className="d-flex justify-content-end text-end fs-10 me-2">
            <Row className="text-body-secondary">
              <div className="pt-1">
                Connected:
                {conversation.status === 'connected' ? (
                  <FontAwesomeIcon
                    icon={faPhoneVolume}
                    className="mx-2 me-0 fs-10 text-success"
                  />
                ) : (
                  <FontAwesomeIcon
                    icon={faPhoneSlash}
                    className="mx-2 me-0 fs-10 text-danger"
                  />
                )}
              </div>
              <div>
                {conversation.status === 'connected' ? (
                  <>
                    Microphone:
                    {isListening ? (
                      <FontAwesomeIcon
                        icon={faMicrophone}
                        className="mx-2 me-1 fs-10 text-success"
                      />
                    ) : (
                      <FontAwesomeIcon
                        icon={faMicrophoneSlash}
                        className="mx-1 me-0 fs-10 text-danger"
                      />
                    )}
                  </>
                ) : null}
              </div>
            </Row>
          </div>
        </Col>
      </Row>

      <div
        ref={messagesContainerRef}
        className="p-2 border rounded scrollbar bg-body-emphasis fs-9 lh-sm flex-grow-1"
        style={{
          overflowY: 'auto',
          scrollBehavior: 'smooth',
          height: '280px'
        }}
      >
        {messages.length > 0 ? (
          <div className="w-full space-y-2">
            {messages.map((msg, index) => {
              // Determine icon based on message context
              let icon = null;
              if (msg.source === 'ai') {
                const messageText = msg.message.toLowerCase();
                if (
                  messageText.includes('appointment') &&
                  messageText.includes('confirm')
                ) {
                  icon = (
                    <FontAwesomeIcon
                      icon={faCalendarCheck}
                      className="text-success me-2"
                    />
                  );
                } else if (
                  messageText.includes('doctor') ||
                  messageText.includes('special')
                ) {
                  icon = (
                    <FontAwesomeIcon
                      icon={faUserMd}
                      className="text-primary me-2"
                    />
                  );
                } else if (
                  messageText.includes('exam') ||
                  messageText.includes('tomogra')
                ) {
                  icon = (
                    <FontAwesomeIcon
                      icon={faMicroscope}
                      className="text-info me-2"
                    />
                  );
                } else if (
                  messageText.includes('locat') ||
                  messageText.includes('center')
                ) {
                  icon = (
                    <FontAwesomeIcon
                      icon={faMapMarkerAlt}
                      className="text-warning me-2"
                    />
                  );
                } else if (
                  messageText.includes('amka') &&
                  messageText.includes('valid')
                ) {
                  icon = (
                    <FontAwesomeIcon
                      icon={faInfoCircle}
                      className="text-success me-2"
                    />
                  );
                }
              }

              return (
                <div
                  key={index}
                  className={`p-2 rounded border mb-2 ${
                    msg.source === 'user'
                      ? 'bg-body mx-10 me-2'
                      : 'bg-body-secondary mx-2 me-10'
                  }`}
                >
                  <small className="text-muted fs-10 d-block mb-1">
                    {msg.timestamp.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit'
                    })}
                  </small>
                  <strong>
                    {msg.source === 'user' ? 'Patient' : 'Ioanna'}:
                  </strong>
                  &nbsp;
                  <span>
                    {icon}
                    {msg.message}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center text-muted p-5">
            {isLoading ? (
              <p>Connecting to agent...</p>
            ) : (
              <p>Press "Start Call" to begin the conversation</p>
            )}
          </div>
        )}
      </div>

      {!hasPermission && (
        <div className="alert alert-warning mt-3 mb-0 p-2 fs-9">
          <FontAwesomeIcon icon={faMicrophoneSlash} className="me-2" />
          Microphone permission required for conversation
        </div>
      )}
    </div>
  );
};

export default HVAHealthCareWidget;
